import React, {Fragment, useEffect, useState} from "react";
import database from "../../../../utils/databaseFirebase";
import UserInfo from "../../../shared/userInfo/user-info";
import {FormattedMessage, useIntl} from "react-intl";
import {IconButton, InputSaveEdit} from "../../../shared";
import './usersMenu.css';
import {OverlayTrigger, Popover} from 'react-bootstrap';
import useOutsideClick from "../../../../utils/useOutsideClick";
import functions from "../../../../utils/functionsFirebase";
import auth from "../../../../utils/authentication";
import groupIndex from "../../index";
import {useDispatch} from "react-redux";

function UsersMenu({group, hasRightManageRoles, roles, hasRightKickUsers}) {

    const intl = useIntl();
    const dispatch = useDispatch();

    const [selectedUser, setSelectedUser] = useState(null);
    const [users, setUsers] = useState([]);
    const [page, setPage] = useState(1);
    const [deletingUser, setDeletingUser] = useState(null);
    const [roleSearch, setRoleSearch] = useState("");

    useEffect(() => {
        const parseObject = (snapshot) => {
            const obj = {...snapshot.val(), id: snapshot.key};
            if (obj.roles) {
                const keys = Object.keys(obj.roles);
                obj.roles = keys.map(x => {
                    return {
                        ref: x, id: obj.roles[x]
                    }
                });
            }
            return obj;
        }

        if (group) {
            const groupUsersRef = database.ref('/group-users/')
                .orderByChild('groupId')
                .equalTo(group.id);

            groupUsersRef.on('child_added', snapshot => {
                const newUser = parseObject(snapshot)
                setUsers(prevUsers => {
                    const aux = [...prevUsers];
                    if (aux.some(x => x.id === newUser.id)) {
                        return prevUsers;
                    }
                    aux.push(newUser);
                    return aux;
                });
            });

            groupUsersRef.on('child_changed', snapshot => {
                const newUser = parseObject(snapshot)
                setUsers(prevUsers => {
                    const aux = [...prevUsers];
                    const editedIndex = aux.findIndex(x => x.id === snapshot.key);
                    aux[editedIndex] = newUser;
                    return aux;
                })
                if (selectedUser?.id === newUser.id) {
                    closePopover();
                }
            });

            groupUsersRef.on('child_removed', snapshot => {

                setUsers(prevUsers => {
                    const aux = [...prevUsers];
                    const editedIndex = aux.findIndex(x => x.id === snapshot.key);
                    aux.splice(editedIndex, 1);
                    return aux;
                })
                if (selectedUser?.id === snapshot.key) {
                    closePopover();
                }
                if (auth.currentUser.uid === snapshot.val().userUid) {
                    dispatch(groupIndex.actions.deletedGroup(group));
                }
            });

            return () => {
                groupUsersRef.off('child_added');
                groupUsersRef.off('child_changed');
                groupUsersRef.off('child_removed');
            }
        }
    }, [group?.id, selectedUser]);


    const [popoverOpen, setPopoverOpen] = useState(false);
    const [activeButton, setActiveButton] = useState(null);

    async function addRoleToUser(user, role) {
        if (!user) {
            closePopover();
            return;
        }
        if (user.loadingAddRole) {
            return;
        }
        closePopover();
        setUsers(prevState => {
            const aux = [...prevState];
            const index = aux.findIndex(x => x.userUid === user.userUid);
            aux[index].loadingAddRole = true;
            return aux;
        });

        const userRole = user.roles?.find(x => x.id === role.id)

        if (userRole) {
            const deleteGroupUserRole = functions.httpsCallable('deleteGroupUserRole');
            await deleteGroupUserRole({groupUserId: user.id, roleRef: userRole.ref, groupId: group.id});
        } else {
            const addGroupUserRole = functions.httpsCallable('addGroupUserRole');
            await addGroupUserRole({groupUserId: user.id, roleId: role.id, groupId: group.id});
        }

    }

    const openPopover = (user, event) => {
        setSelectedUser(user)
        setRoleSearch("");
        setPopoverOpen(true);
        setActiveButton(event.target.id);
    };

    const closePopover = () => {
        setSelectedUser(null);
        setPopoverOpen(false);
        setActiveButton(null);
    };

    const deselectDeleteUser = () => {
        setDeletingUser(null);
    }

    const popover = (
        <Popover id="popover-roles">
            <Popover.Body>
                <InputSaveEdit inputValue={roleSearch} showLabel={false} onSave={value => setRoleSearch(value)}
                               label={intl.formatMessage({id: "app.search"})} alwaysEditing={true}
                               id="searchTagInput" required={false}></InputSaveEdit>
                <ul className="list-group mt-1" style={{maxHeight: "160px", overflowY: "auto"}}>
                    {roles?.filter(x => !x.everyone).map(role =>
                        <div key={role.id} onClick={() => addRoleToUser(selectedUser, role)}
                             style={{cursor: "pointer", userSelect: "none"}}
                             className="list-group-item list-group-item-action d-flex flex-row justify-content-between">
                            {role.roleName}
                            <span>
                            {selectedUser?.roles?.some(x => x.id === role.id) ?
                                <i className="fa-solid fa-circle-xmark ms-2 text-danger"></i> :
                                <i className="fa-solid fa-circle-plus ms-2"></i>}
                            </span>
                        </div>)}
                </ul>
            </Popover.Body>
        </Popover>
    );

    // Use the custom hook to handle clicks outside the popover
    useOutsideClick("popover-roles", closePopover);
    useOutsideClick("accept-kick-user", deselectDeleteUser);

    function canKickUser(user) {
        if (!auth.currentUser)
            return false;
        const userRoles = group.roles?.filter(x => user.roles?.some(y => x.id === y.id));
        const isCreator = user.userUid === group.uidCreator;
        const isSameUser = user.userUid === auth.currentUser.uid;
        const imCreator = auth.currentUser.uid === group.uidCreator;
        const isAdmin = userRoles?.some(x => userRoles.some(x => x.administrator));
        return (hasRightKickUsers && !isCreator && !isSameUser && (!isAdmin || imCreator));
    }

    function canModifyUserRoles(user) {
        if (!auth.currentUser)
            return false;
        const userRoles = group.roles?.filter(x => user.roles?.some(y => x.id === y.id));
        const isCreator = user.userUid === group.uidCreator;
        const imCreator = auth.currentUser.uid === group.uidCreator;
        const isAdmin = userRoles?.some(x => userRoles.some(x => x.administrator));
        return (hasRightManageRoles && (!isCreator || imCreator) && (!isAdmin || imCreator || auth.currentUser.uid === user.userUid));
    }

    async function confirmKick(user) {
        if (!auth.currentUser)
            return false;
        setDeletingUser({loading: true, ...user});
        const kickUser = functions.httpsCallable('kickUserFromGroup');
        await kickUser({groupId: group.id, kickedUid: user.userUid});
        deselectDeleteUser();
    }

    return (
        <div>
            <div style={{overflowY: "auto", maxHeight: "56vh"}}>
                <ul className="list-group list-group-flush position-relative">
                    {users.slice(0, 7 * page).map(user => {
                        return <li key={user.userUid} className="list-group-item" id={user.userUid}>
                            <UserInfo initialUser={null}
                                      loading={deletingUser?.userUid === user.userUid && deletingUser.loading}
                                      uid={user.userUid}
                                      className={deletingUser?.userUid === user.userUid && " text-danger fw-bold"}
                                      nameReplace={deletingUser?.userUid === user.userUid &&
                                          "users-menu.kick-user.confirmation"
                                      }
                                      nameDecorations={group.uidCreator === user.userUid &&
                                          <span className="text-end">
                                                <span style={{color: "gold"}}>
                                                    <i className="fa-solid fa-crown ms-2"
                                                       title={intl.formatMessage({id: "groups.group-leader"})}></i>
                                                </span>
                                          </span>}
                                      nameOptions={canKickUser(user) &&
                                          <span>
                                                {deletingUser?.userUid !== user.userUid && <IconButton
                                                    style={{paddingInline: "6px"}}
                                                    icon="fa-solid fa-door-closed"
                                                    title="Kick user"
                                                    className="text-danger" id="button-kick-user"
                                                    onClick={() => {
                                                        setDeletingUser(user);
                                                    }}/>}
                                              {deletingUser?.userUid === user.userUid &&
                                                  <div className="d-flex flex-row">
                                                      <IconButton
                                                          icon="fa-solid fa-circle-check"
                                                          title={"Confirm kick"}
                                                          className="text-danger" id="accept-kick-user"
                                                          onClick={() => {
                                                              confirmKick(user);
                                                          }}/>
                                                      <IconButton
                                                          style={{paddingInline: "6px"}}
                                                          icon="fa-solid fa-circle-xmark"
                                                          title={"Cancel kick"}
                                                          className="text-secondary ms-1" id="cancel-kick-user"
                                                          onClick={() => {
                                                              setDeletingUser(null);
                                                          }}/>
                                                  </div>}
                                          </span>}
                                      userExtraInfo={
                                          <div>
                                              {user.roles?.map(roleId => roles.find(r => r.id === roleId.id)).filter(x => x && !x.everyone)
                                                  .map(role => <span className="badge text-bg-secondary ms-1"
                                                                     key={role.id}>{role.roleName}</span>)}
                                              {canModifyUserRoles(user) &&
                                                  <OverlayTrigger
                                                      trigger="click" // You can change this to "hover", "focus", or any combination of them
                                                      placement="left" // Change this to the desired placement
                                                      overlay={popover}
                                                      show={popoverOpen && activeButton === user.userUid && !user.loadingAddRole}
                                                      rootClose
                                                      onHide={closePopover}
                                                  >
                                                      <button disabled={user.roles?.length > 5 || roles?.length < 2}
                                                              onClick={e => openPopover(user, e)} id={user.userUid}
                                                              style={{background: "none", border: "none"}}>
                                                          <i id={user.userUid}
                                                             className={"fa-solid " + (!user.loadingAddRole ? "fa-circle-plus" : "fa-spinner fa-spin") + " fa-lg"}></i>
                                                      </button>
                                                  </OverlayTrigger>}
                                          </div>
                                      }/>
                        </li>
                    })}
                </ul>
            </div>
            <div className='text-end mt-2'>
                {users.length > 7 * page &&
                    <Fragment>
                        <small>{users.length - 7 * page} <FormattedMessage
                            id="users-menu.more-users.info"/></small> &nbsp;
                        <small>
                            <a className="text-primary" style={{cursor: "pointer"}}
                               onClick={() => setPage(page + 1)}>[<FormattedMessage
                                id="group-post.description.show-more"/>]</a>
                        </small>
                    </Fragment>}
            </div>
        </div>
    )
}

export default UsersMenu;
